/**
 * Table view: one row per lot with the same figures as the card. One column
 * group per selected model (verdict, max bid, the model's own two headline
 * figures), then the evidence scores; sortable through the same server sorts.
 * A saved result that is not the latest calculation carries the stale mark.
 */
import { Fragment } from 'react'
import { Link } from '@tanstack/react-router'
import { IconArrowDown, IconArrowUp } from '@tabler/icons-react'
import type { CardItem, CardSort, SelectedDealModel } from '../../api/types'
import { effectivePropertyClassification, propertyClassificationLabel } from '../../api/propertyClassification'
import { area, dateShort, humanize, money, moneyExact, photoVariant } from '../../api/format'
import { TILE_LABELS, type ExperienceSpec, type TileKey } from '../../experiences/registry'
import { Verdict } from '../../ui/kit'
import { StaleMark } from '../../ui/StaleMark'
import { VisitedTag, modelRows, scoreFor, tileClass } from './LotCard'
import { headlineOutputs } from './modelOutputs'

const SORT_FOR_COLUMN: Partial<Record<string, { asc: CardSort; desc: CardSort }>> = {
  price: { asc: 'price_asc', desc: 'price_desc' },
  auction: { asc: 'auction_asc', desc: 'auction_asc' },
  resale: { asc: 'potential_desc', desc: 'potential_desc' },
  rent: { asc: 'rental_potential_desc', desc: 'rental_potential_desc' },
}

export function TableView({ items, models, spec, listId, sort, onSort }: { items: CardItem[]; models: SelectedDealModel[]; spec: ExperienceSpec; listId?: string; sort: CardSort; onSort: (sort: CardSort) => void }) {
  const sortable = (column: string) => {
    const s = SORT_FOR_COLUMN[column]
    if (!s || (!spec.register.sorts.includes(s.asc) && !spec.register.sorts.includes(s.desc))) return null
    const on = sort === s.asc || sort === s.desc
    return { on, next: () => onSort(sort === s.desc && s.asc !== s.desc ? s.asc : s.desc), dir: sort === s.asc && s.asc !== s.desc ? 'asc' : 'desc' }
  }
  const Head = ({ column, children, num, className }: { column: string; children: React.ReactNode; num?: boolean; className?: string }) => {
    const s = sortable(column)
    return <th className={[num ? 'num' : '', className ?? ''].join(' ').trim()} aria-sort={s?.on ? (s.dir === 'asc' ? 'ascending' : 'descending') : undefined}>{s ? <button type="button" className={s.on ? 'is-on' : ''} onClick={s.next}>{children}{s.on ? (s.dir === 'asc' ? <IconArrowUp className="ic-sm" /> : <IconArrowDown className="ic-sm" />) : null}</button> : children}</th>
  }
  const showModels = spec.card.strip !== 'none' && models.length > 0
  const modelHeads = models.map(model => { const [h1, h2] = headlineOutputs(model); return { model, h1: h1?.label ?? 'Profit', h2: h2?.label ?? null } })
  const alt = (i: number) => (i % 2 === 1 ? 'alt' : '')
  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <thead>
          {showModels ? (
            <tr>
              <th colSpan={3} />
              {modelHeads.map((h, i) => <th key={h.model.logicalModelId} className={['mg', alt(i)].join(' ').trim()} colSpan={h.h2 ? 4 : 3}>{h.model.modelName ?? 'Model'}</th>)}
              <th colSpan={spec.card.tiles.length} className="mg">Evidence</th>
              <th colSpan={4} />
            </tr>
          ) : null}
          <tr>
            <th />
            <th>Address</th>
            <Head column="price" num>{spec.card.photoStrip === 'bid' ? 'Marketed' : 'Price'}</Head>
            {showModels ? modelHeads.map((h, i) => <Fragment key={h.model.logicalModelId}><th className={['first', alt(i)].join(' ').trim()}>Verdict</th><th className={['num', alt(i)].join(' ').trim()}>Max bid</th><th className={['num', alt(i)].join(' ').trim()}>{h.h1}</th>{h.h2 ? <th className={['num', alt(i)].join(' ').trim()}>{h.h2}</th> : null}</Fragment>) : null}
            {spec.card.tiles.map((key, i) => <Head key={key} column={key} className={i === 0 ? 'first' : undefined}>{TILE_LABELS[key]}</Head>)}
            <th className="first">Facts</th>
            <Head column="auction">Auction</Head>
            <th>Source</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map(item => {
            const p = item.card?.property
            const classification = effectivePropertyClassification(p ?? {})
            const listing = item.card?.listing
            const photo = item.photoUrls?.[0] ?? listing?.coverUrl ?? null
            const rows = modelRows(item, models, listing?.marketedPricePence ?? null)
            const address = [p?.address?.line1, p?.address?.city].filter(Boolean).join(', ') || 'Address unavailable'
            const personal = item.personalSettings ?? null
            const to = { to: '/properties/$propertyId' as const, params: { propertyId: item.propertyId }, search: listId ? { listId } : {} }
            return (
              <tr key={item.propertyId}>
                <td className="ph"><Link {...to} className="thumb" style={photo ? { backgroundImage: `url(${photoVariant(photo, 'small')})` } : undefined} aria-label={`Open ${address}`} /></td>
                <td><Link {...to} className="addr">{address}<small>{p?.address?.postcode ?? ''}{listing?.listingStatus ? ` · ${humanize(listing.listingStatus)}` : ''}</small></Link></td>
                <td className="num">{money(listing?.marketedPricePence)}</td>
                {showModels ? rows.map((r, i) => <Fragment key={r.model.logicalModelId}>
                  <td className={['first', alt(i)].join(' ').trim()}><span className="tbl-verdict">{r.verdict ? <Verdict band={r.verdict.cls}>{r.verdict.label}</Verdict> : <span className="t-faint">{r.status}</span>}{r.stale.stale ? <StaleMark interactive reasons={r.stale.reasons} /> : null}</span></td>
                  <td className={['num', alt(i)].join(' ').trim()}>{r.maxBid !== null ? moneyExact(r.maxBid) : '—'}</td>
                  <td className={['num', alt(i)].join(' ').trim()}>{r.verdict ? r.figures.h1 : '—'}</td>
                  {modelHeads[i]?.h2 ? <td className={['num', alt(i)].join(' ').trim()}>{r.verdict ? r.figures.h2 : '—'}</td> : null}
                </Fragment>) : null}
                {spec.card.tiles.map((key: TileKey, i) => { const s = scoreFor(item, key); return <td key={key} className={i === 0 ? 'first' : undefined}><span className={`sc ${tileClass(s.value)}`} title={s.partial ? 'Partial evidence' : undefined}>{s.value ?? '—'}{s.partial ? '▲' : ''}</span></td> })}
                <td className="t-muted first">{[propertyClassificationLabel(classification, true), classification.bedrooms !== null ? `${classification.bedrooms} bed` : null, humanize(p?.tenure), area(item.card?.valuation?.floorAreaSqm, item.card?.valuation?.floorAreaAssumed)].filter(Boolean).join(' · ')}</td>
                <td>{dateShort(listing?.auctionDate) ?? <span className="t-faint">—</span>}</td>
                <td><span className="src" title={listing?.source ?? ''}>{listing?.source ?? '—'}</span></td>
                <td className="act">
                  <VisitedTag visited={personal?.visited} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
